import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { Dayjs } from "dayjs";
import dayjs, { MOLDOVA_TIMEZONE } from "../../utils/dayjsTimezone";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import { Candidate } from "../../components/HireCRM/CandidateBoard/types/CandidateBoardTypes";

interface ScheduledCandidate extends Candidate {
  scheduledFor: string;
}

const fetchScheduledCandidates = async (
  token: string | null | undefined,
): Promise<ScheduledCandidate[]> => {
  const response = await fetch(`${apiVariable}/candidates/scheduled`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching scheduled candidates");
  }
  return response.json();
};

const buildCalendar = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const endOfMonth = month.endOf("month");
  const daysInMonth = endOfMonth.date();
  const startWeekday = startOfMonth.day();

  const cells: Array<Dayjs | null> = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(startOfMonth.date(day));
  }

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let i = remainder; i < 7; i += 1) {
      cells.push(null);
    }
  }

  return cells;
};

const WEEKDAY_LABELS = [
  { key: "sun", label: "S" },
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
];

const ScheduledCandidatesPage = () => {
  const { token } = useAuth();
  const initialMonth = dayjs().tz(MOLDOVA_TIMEZONE).startOf("month");
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(initialMonth);
  const [selectedDay, setSelectedDay] = useState<Dayjs | null>(null);

  const {
    data: scheduledCandidates = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["scheduledCandidates"],
    queryFn: () => fetchScheduledCandidates(token),
  });

  const scheduledWithDates = useMemo(
    () =>
      scheduledCandidates
        .filter((candidate) => candidate.scheduledFor)
        .map((candidate) => ({
          candidate,
          scheduledDate: dayjs.utc(candidate.scheduledFor).tz(MOLDOVA_TIMEZONE),
        })),
    [scheduledCandidates],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map<string, { date: Dayjs; items: ScheduledCandidate[] }>();
    scheduledWithDates.forEach(({ candidate, scheduledDate }) => {
      if (!scheduledDate.isValid()) {
        return;
      }
      const key = scheduledDate.format("YYYY-MM-DD");
      const existing = map.get(key);
      const updatedCandidate = {
        ...candidate,
        scheduledFor: scheduledDate.toISOString(),
      };
      if (existing) {
        existing.items.push(updatedCandidate);
        existing.items.sort((a, b) =>
          dayjs(a.scheduledFor).isAfter(dayjs(b.scheduledFor)) ? 1 : -1,
        );
      } else {
        map.set(key, {
          date: scheduledDate.startOf("day"),
          items: [updatedCandidate],
        });
      }
    });
    return map;
  }, [scheduledWithDates]);

  const calendarCells = useMemo(
    () => buildCalendar(currentMonth),
    [currentMonth],
  );

  useEffect(() => {
    if (selectedDay) {
      return;
    }
    const firstMatch = scheduledWithDates.find(({ scheduledDate }) =>
      scheduledDate.isSame(currentMonth, "month"),
    );
    if (firstMatch) {
      setSelectedDay(firstMatch.scheduledDate.startOf("day"));
    }
  }, [currentMonth, scheduledWithDates, selectedDay]);

  const selectedKey = selectedDay?.format("YYYY-MM-DD") ?? "";
  const selectedItems = selectedKey
    ? (eventsByDay.get(selectedKey)?.items ?? [])
    : [];

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-GB", {
        timeZone: MOLDOVA_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
    setSelectedDay(null);
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (value) {
      setCurrentMonth(value.startOf("month"));
      setSelectedDay(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 0, sm: 3 } }}>
        <Typography color="error">
          {(error as Error).message || "Failed to load scheduled candidates."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 } }}>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <Button variant="outlined" onClick={handlePrevMonth}>
          Previous
        </Button>
        <Typography variant="h5" sx={{ minWidth: 200 }} align="center">
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        <Button variant="outlined" onClick={handleNextMonth}>
          Next
        </Button>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={["year", "month"]}
            label="Jump to"
            value={currentMonth}
            onChange={handleDateChange}
          />
        </LocalizationProvider>
      </Stack>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={7}>
          <Paper sx={{ p: { xs: 0.5, sm: 2 } }}>
            <Grid container columns={7} spacing={{ xs: 0.25, sm: 1 }} mb={1}>
              {WEEKDAY_LABELS.map(({ key, label }) => (
                <Grid item xs={1} key={key}>
                  <Typography variant="subtitle2" align="center">
                    {label}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Grid container columns={7} spacing={{ xs: 0.25, sm: 1 }}>
              {calendarCells.map((cell, index) => {
                if (!cell) {
                  return <Grid item xs={1} key={`empty-${index}`} />;
                }
                const key = cell.format("YYYY-MM-DD");
                const itemsForDay = eventsByDay.get(key)?.items ?? [];
                const isSelected = selectedDay?.isSame(cell, "day");
                return (
                  <Grid item xs={1} key={key}>
                    <Paper
                      variant={isSelected ? "outlined" : undefined}
                      sx={{
                        p: { xs: 0, sm: 1 },
                        minHeight: 44,
                        cursor: itemsForDay.length ? "pointer" : "default",
                        backgroundColor: itemsForDay.length
                          ? "rgba(25, 118, 210, 0.08)"
                          : "inherit",
                        borderColor: isSelected ? "primary.main" : undefined,
                      }}
                      onClick={() => {
                        if (itemsForDay.length) {
                          setSelectedDay(cell);
                        }
                      }}
                    >
                      <Stack alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle1">
                          {cell.date()}
                        </Typography>
                        {itemsForDay.length > 0 && (
                          <Chip
                            color="primary"
                            size="small"
                            label={
                              <>
                                {itemsForDay.length}
                                <Box
                                  component="span"
                                  sx={{ display: { xs: "none", sm: "inline" } }}
                                >
                                  {" "}
                                  scheduled
                                </Box>
                              </>
                            }
                          />
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4} lg={5}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              {selectedDay
                ? `Schedule for ${selectedDay.format("MMMM D, YYYY")}`
                : "Select a day to view the schedule"}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {selectedDay && selectedItems.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No candidates scheduled for this day.
              </Typography>
            )}
            {selectedDay && selectedItems.length > 0 && (
              <List>
                {selectedItems.map((item) => {
                  const time = formatter.format(new Date(item.scheduledFor));
                  return (
                    <ListItem key={item.id} sx={{ alignItems: "flex-start" }}>
                      <ListItemText
                        primary={`${time} – ${item.firstName} ${item.lastName}`}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              {item.position}
                            </Typography>
                            {item.phoneNumber && (
                              <>
                                <br />
                                Phone: {item.phoneNumber}
                              </>
                            )}
                            {item.email && (
                              <>
                                <br />
                                Email: {item.email}
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
            {!selectedDay && (
              <Typography variant="body2" color="textSecondary">
                Use the calendar to choose a day and review the detailed time
                slots.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScheduledCandidatesPage;
